import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '@/api/auth';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-mono-950 flex items-center justify-center py-12 px-4">
        <div className="max-w-sm w-full text-center space-y-6">
          <div className="border border-red-500/30 bg-red-500/10 p-6">
            <p className="text-xs text-red-400 tracking-wider mb-2">INVALID LINK</p>
            <p className="text-[11px] text-mono-400 leading-relaxed">
              This password reset link is invalid or has expired.
            </p>
          </div>
          <Link
            to="/forgot-password"
            className="inline-block text-[10px] text-cyan-400 hover:text-cyan-300 tracking-wider"
          >
            REQUEST A NEW LINK
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('PASSWORD MUST BE AT LEAST 8 CHARACTERS');
      return;
    }

    if (password !== confirmPassword) {
      setError('PASSWORDS DO NOT MATCH');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      navigate('/login?message=Password+reset+successfully.+Please+sign+in.', { replace: true });
    } catch {
      setError('INVALID OR EXPIRED RESET LINK. PLEASE REQUEST A NEW ONE.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-mono-950 flex items-center justify-center py-12 px-4">
      <div className="max-w-sm w-full">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-mono-100 tracking-[0.4em] font-orbitron">ARGOS</h1>
          <p className="text-mono-500 text-xs tracking-widest mt-2 font-orbitron">SET NEW PASSWORD</p>
          <div className="mt-8 h-px bg-mono-800" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="border border-red-500/30 bg-red-500/10 p-3 text-center">
              <p className="text-xs text-red-400 tracking-wider">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-[10px] text-mono-500 tracking-widest mb-2">
              NEW PASSWORD
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 bg-mono-900 border border-mono-700 text-mono-200 text-sm focus:border-cyan-500/50 focus:outline-none tracking-wide"
              placeholder="Minimum 8 characters"
              autoFocus
              autoComplete="new-password"
            />
          </div>

          <div>
            <label className="block text-[10px] text-mono-500 tracking-widest mb-2">
              CONFIRM PASSWORD
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 bg-mono-900 border border-mono-700 text-mono-200 text-sm focus:border-cyan-500/50 focus:outline-none tracking-wide"
              placeholder="Re-enter password"
              autoComplete="new-password"
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 border border-cyan-500 bg-cyan-500 text-black text-xs font-medium tracking-widest hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'RESETTING...' : 'RESET PASSWORD'}
            </button>
          </div>
        </form>

        <div className="mt-8 text-center">
          <Link
            to="/login"
            className="text-[10px] text-mono-500 hover:text-mono-300 tracking-wider"
          >
            BACK TO LOGIN
          </Link>
        </div>
      </div>
    </div>
  );
}
